<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

const query = ref('SELECT * FROM users LIMIT 10;')
const results = ref(null)
const loading = ref(false)
const error = ref('')
const queryHistory = ref([])
const savedQueries = ref([])
const showSaveModal = ref(false)
const saveQueryName = ref('')
const executionTime = ref(0)
const showHistory = ref(false)

// Load saved queries and history from localStorage
onMounted(() => {
  const saved = localStorage.getItem('db-saved-queries')
  if (saved) {
    savedQueries.value = JSON.parse(saved)
  }
  
  const history = localStorage.getItem('db-query-history')
  if (history) {
    queryHistory.value = JSON.parse(history)
  }
})

const executeQuery = async () => {
  if (!query.value.trim()) {
    error.value = 'Please enter a query'
    return
  }

  loading.value = true
  error.value = ''
  results.value = null
  
  const startTime = Date.now()
  
  try {
    const response = await axios.post('/api/database-query/execute', {
      query: query.value
    })
    
    executionTime.value = Date.now() - startTime
    results.value = response.data
    
    // Add to history
    const historyEntry = {
      id: Date.now(),
      query: query.value,
      timestamp: new Date().toISOString(),
      executionTime: executionTime.value,
      rowCount: response.data.data?.length || 0
    }
    
    queryHistory.value.unshift(historyEntry)
    if (queryHistory.value.length > 50) {
      queryHistory.value = queryHistory.value.slice(0, 50)
    }
    
    localStorage.setItem('db-query-history', JSON.stringify(queryHistory.value))
    
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    executionTime.value = Date.now() - startTime
  } finally {
    loading.value = false
  }
}

const saveQuery = () => {
  if (!saveQueryName.value.trim()) {
    alert('Please enter a name for the query')
    return
  }
  
  const savedQuery = {
    id: Date.now(),
    name: saveQueryName.value,
    query: query.value,
    createdAt: new Date().toISOString()
  }
  
  savedQueries.value.push(savedQuery)
  localStorage.setItem('db-saved-queries', JSON.stringify(savedQueries.value))
  
  showSaveModal.value = false
  saveQueryName.value = ''
}

const loadQuery = (queryText) => {
  query.value = queryText
}

const loadFromHistory = (historyItem) => {
  query.value = historyItem.query
  showHistory.value = false
}

const deleteSavedQuery = (queryId) => {
  savedQueries.value = savedQueries.value.filter(q => q.id !== queryId)
  localStorage.setItem('db-saved-queries', JSON.stringify(savedQueries.value))
}

const clearHistory = () => {
  queryHistory.value = []
  localStorage.removeItem('db-query-history')
}

const exportResults = () => {
  if (!results.value?.data) return
  
  const csv = [
    results.value.columns.map(col => col.name).join(','),
    ...results.value.data.map(row => 
      results.value.columns.map(col => `"${row[col.name] || ''}"`).join(',')
    )
  ].join('\n')
  
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `query-results-${Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const commonQueries = [
  {
    name: 'Show all tables',
    query: 'SHOW TABLES;'
  },
  {
    name: 'Describe table',
    query: 'DESCRIBE table_name;'
  },
  {
    name: 'Count rows',
    query: 'SELECT COUNT(*) as total_rows FROM table_name;'
  },
  {
    name: 'Show table structure',
    query: 'SHOW CREATE TABLE table_name;'
  },
  {
    name: 'Recent records',
    query: 'SELECT * FROM table_name ORDER BY created_at DESC LIMIT 10;'
  }
]

const insertCommonQuery = (commonQuery) => {
  query.value = commonQuery.query
}

// Simple syntax highlighting for display
const highlightedQuery = computed(() => {
  let highlighted = query.value
  
  // SQL keywords
  const keywords = ['SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'ON', 'AS', 'AND', 'OR', 'ORDER', 'BY', 'GROUP', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'LIKE', 'IN', 'BETWEEN', 'IS', 'NULL', 'NOT', 'EXISTS', 'UNION', 'CREATE', 'ALTER', 'DROP', 'TABLE', 'DATABASE', 'INDEX', 'VIEW', 'SHOW', 'DESCRIBE', 'DESC']
  
  keywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi')
    highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${keyword}</span>`)
  })
  
  return highlighted
})
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="bg-white border-b border-gray-200 p-6">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">SQL Query Maker</h3>
          <p class="text-gray-600 mt-1">Execute custom SQL queries and view results</p>
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="showHistory = !showHistory"
            class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <i class="fas fa-history mr-2"></i>
            History
          </button>
          <button
            @click="showSaveModal = true"
            :disabled="!query.trim()"
            class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <i class="fas fa-save mr-2"></i>
            Save Query
          </button>
          <button
            @click="executeQuery"
            :disabled="loading || !query.trim()"
            class="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <i class="fas fa-play mr-2" :class="loading ? 'fa-spinner fa-spin' : ''"></i>
            {{ loading ? 'Executing...' : 'Execute' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Query Input Area -->
    <div class="bg-white border-b border-gray-200 p-6">
      <div class="flex space-x-6">
        <!-- Query Editor -->
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">SQL Query</label>
          <div class="relative">
            <textarea
              v-model="query"
              class="w-full h-32 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Enter your SQL query here..."
            ></textarea>
          </div>
          
          <!-- Query Info -->
          <div class="mt-2 flex items-center justify-between text-sm text-gray-500">
            <span>{{ query.length }} characters</span>
            <div class="flex items-center space-x-4">
              <span v-if="executionTime">Execution time: {{ executionTime }}ms</span>
              <span v-if="results?.data">{{ results.data.length }} rows returned</span>
            </div>
          </div>
        </div>

        <!-- Common Queries & Saved Queries -->
        <div class="w-80">
          <div class="space-y-4">
            <!-- Common Queries -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Common Queries</label>
              <div class="space-y-1 max-h-32 overflow-y-auto">
                <button
                  v-for="commonQuery in commonQueries"
                  :key="commonQuery.name"
                  @click="insertCommonQuery(commonQuery)"
                  class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  {{ commonQuery.name }}
                </button>
              </div>
            </div>

            <!-- Saved Queries -->
            <div v-if="savedQueries.length > 0">
              <label class="block text-sm font-medium text-gray-700 mb-2">Saved Queries</label>
              <div class="space-y-1 max-h-32 overflow-y-auto">
                <div
                  v-for="savedQuery in savedQueries"
                  :key="savedQuery.id"
                  class="flex items-center justify-between px-3 py-2 hover:bg-gray-100 rounded group"
                >
                  <button
                    @click="loadQuery(savedQuery.query)"
                    class="flex-1 text-left text-sm text-gray-700 truncate"
                    :title="savedQuery.query"
                  >
                    {{ savedQuery.name }}
                  </button>
                  <button
                    @click="deleteSavedQuery(savedQuery.id)"
                    class="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i class="fas fa-trash text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="error" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-start">
          <i class="fas fa-exclamation-triangle text-red-500 mt-1 mr-3"></i>
          <div>
            <h4 class="text-red-800 font-medium">Query Error</h4>
            <p class="text-red-700 text-sm mt-1">{{ error }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Area -->
    <div class="flex-1 overflow-hidden">
      <div v-if="loading" class="flex items-center justify-center h-full">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4"></i>
          <p class="text-gray-600">Executing query...</p>
        </div>
      </div>

      <div v-else-if="results" class="h-full flex flex-col">
        <!-- Results Header -->
        <div class="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-lg font-medium text-gray-900">Query Results</h4>
              <p class="text-sm text-gray-600">
                {{ results.data?.length || 0 }} rows in {{ executionTime }}ms
              </p>
            </div>
            <button
              v-if="results.data?.length > 0"
              @click="exportResults"
              class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center"
            >
              <i class="fas fa-download mr-2"></i>
              Export CSV
            </button>
          </div>
        </div>

        <!-- Results Table -->
        <div class="flex-1 overflow-auto">
          <table v-if="results.data?.length > 0" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th
                  v-for="column in results.columns"
                  :key="column.name"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ column.name }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(row, index) in results.data" :key="index" class="hover:bg-gray-50">
                <td
                  v-for="column in results.columns"
                  :key="column.name"
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                >
                  <div class="max-w-xs truncate" :title="row[column.name]">
                    {{ row[column.name] }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="text-center py-12">
            <i class="fas fa-check-circle text-4xl text-green-500 mb-4"></i>
            <p class="text-gray-500">Query executed successfully</p>
            <p class="text-gray-400 text-sm">{{ results.message || 'No rows returned' }}</p>
          </div>
        </div>
      </div>

      <div v-else class="flex items-center justify-center h-full text-gray-500">
        <div class="text-center">
          <i class="fas fa-code text-6xl mb-4"></i>
          <p class="text-xl">Ready to execute SQL queries</p>
          <p class="text-sm text-gray-400 mt-2">Write your query above and click Execute</p>
        </div>
      </div>
    </div>

    <!-- History Sidebar -->
    <div v-if="showHistory" class="fixed inset-y-0 right-0 w-96 bg-white shadow-lg border-l border-gray-200 z-50">
      <div class="h-full flex flex-col">
        <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">Query History</h3>
            <button @click="showHistory = false" class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="queryHistory.length === 0" class="text-center py-12 text-gray-500">
            <i class="fas fa-history text-4xl mb-4"></i>
            <p>No query history yet</p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="historyItem in queryHistory"
              :key="historyItem.id"
              class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
              @click="loadFromHistory(historyItem)"
            >
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm text-gray-500">
                  {{ new Date(historyItem.timestamp).toLocaleString() }}
                </span>
                <div class="text-xs text-gray-400">
                  {{ historyItem.executionTime }}ms • {{ historyItem.rowCount }} rows
                </div>
              </div>
              <div class="text-sm font-mono text-gray-700 truncate">
                {{ historyItem.query }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="queryHistory.length > 0" class="border-t border-gray-200 p-4">
          <button
            @click="clearHistory"
            class="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm"
          >
            Clear History
          </button>
        </div>
      </div>
    </div>

    <!-- Save Query Modal -->
    <div v-if="showSaveModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">Save Query</h3>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Query Name</label>
          <input
            v-model="saveQueryName"
            type="text"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter a name for this query"
            @keyup.enter="saveQuery"
          >
        </div>

        <div class="flex justify-end space-x-3">
          <button
            @click="showSaveModal = false; saveQueryName = ''"
            class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            @click="saveQuery"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Query
          </button>
        </div>
      </div>
    </div>
  </div>
</template> 